"use client"

import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

import type { SearchRecentEntry } from "@/features/search/model/search-palette-types"

export const SEARCH_RECENTS_CAP = 8

/** Recents belong to one workspace + branch: a result opened in one branch is not offered in another. */
export function searchRecentsScopeKey(workspaceId: string | null | undefined, branchId: string | null | undefined): string | null {
  if (!workspaceId || !branchId) return null
  return `${workspaceId}:${branchId}`
}

type SearchRecentsState = {
  byScope: Record<string, SearchRecentEntry[]>
  pushRecent: (scopeKey: string, entry: SearchRecentEntry) => void
  clearRecents: (scopeKey: string) => void
}

/**
 * The palette's "Recents", kept on this device only. Stores just id, kind, title and url: enough to show and
 * reopen an entry, never the record's contents.
 */
export const useSearchRecentsStore = create<SearchRecentsState>()(
  persist(
    (set) => ({
      byScope: {},
      pushRecent: (scopeKey, entry) =>
        set((state) => {
          const current = state.byScope[scopeKey] ?? []
          const kept: SearchRecentEntry = { id: entry.id, kind: entry.kind, title: entry.title, url: entry.url }
          const next = [kept, ...current.filter((item) => !(item.id === kept.id && item.kind === kept.kind))].slice(
            0,
            SEARCH_RECENTS_CAP,
          )
          return { byScope: { ...state.byScope, [scopeKey]: next } }
        }),
      clearRecents: (scopeKey) =>
        set((state) => {
          const next = { ...state.byScope }
          delete next[scopeKey]
          return { byScope: next }
        }),
    }),
    {
      name: "tenant-global-search-recents-v1",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ byScope: state.byScope }),
    },
  ),
)

const NO_RECENTS: SearchRecentEntry[] = []

export function selectSearchRecents(scopeKey: string | null) {
  return (state: SearchRecentsState): SearchRecentEntry[] => (scopeKey ? (state.byScope[scopeKey] ?? NO_RECENTS) : NO_RECENTS)
}
